/*
 * Copyright 2026 Intuition Robotics ltd
 */
import {Module} from "@intuitionrobotics/ts-common";
import {ThunderDispatcher, ToastModule, XhrHttpModule} from "@intuitionrobotics/thunderstorm/frontend";
import {HttpMethod} from "@intuitionrobotics/thunderstorm";
import dayjs from "../app/utils/dayjs";
import {
    Api_CheckinMorningActivity,
    Api_CheckinOverride,
    Api_CheckinTriggerDaily,
    CheckinActivityItem
} from "@app/app-shared/check-in";

export interface OnCheckinUpdated {
    __onCheckinUpdated: () => void;
}

export const dispatch_onCheckinUpdated =
    new ThunderDispatcher<OnCheckinUpdated, "__onCheckinUpdated">("__onCheckinUpdated");

const WINDOW_START_HOUR = 5;

// Device pushes runtime status roughly every 10 min. 15 min lets one push slip
// before we mark the unit offline — one missed push + slack — which matches
// the freshness budget Pavel signed off on for Care Center connectivity.
const ONLINE_THRESHOLD_MS = 15 * 60 * 1000;

class CheckinModule_Class extends Module {
    private byUnit: { [unitId: string]: CheckinActivityItem } = {};
    private fetched = new Set<string>();

    constructor() {
        super("CheckinModule");
    }

    get = (unitId: string): CheckinActivityItem | undefined => this.byUnit[unitId];

    // True once every unitId's fetch has resolved (success or error). The upstream may
    // omit units with no activity, so we can't gate on byUnit — we'd loop forever.
    hasDataFor = (unitIds: string[]): boolean =>
        unitIds.length > 0 && unitIds.every(id => this.fetched.has(id));

    fetchMany = (unitIds: string[]) => {
        if (!unitIds.length) return;
        XhrHttpModule
            .createRequest<Api_CheckinMorningActivity>(HttpMethod.POST, "checkin-morning-activity")
            .setRelativeUrl("/v1/check-in/morning-activity")
            .setLabel(`Fetching check-in activity (${unitIds.length} customers)`)
            .setJsonBody({unitIds})
            .setOnError(() => {
                unitIds.forEach(id => this.fetched.add(id));
                dispatch_onCheckinUpdated.dispatchUI();
                return true;
            })
            .execute((items: CheckinActivityItem[]) => {
                items.forEach(item => this.byUnit[item.agentId] = item);
                unitIds.forEach(id => this.fetched.add(id));
                dispatch_onCheckinUpdated.dispatchUI();
            });
    };

    markCheckedIn = (unitId: string, onDone?: (ok: boolean) => void) => XhrHttpModule
        .createRequest<Api_CheckinOverride>(HttpMethod.POST, "checkin-override")
        .setRelativeUrl("/v1/check-in/override")
        .setLabel(`Marking ${unitId} checked in`)
        .setJsonBody({unitId})
        .setOnError(() => {
            ToastModule.toastError("Couldn't mark as checked in — please try again");
            onDone?.(false);
            return true;
        })
        .execute(() => {
            this.fetchMany([unitId]);
            onDone?.(true);
        });

    /** Hack: SuperAdmin-only. Fires DailyCheckInModule.onScheduledEvent — same as the cron. */
    triggerDaily = (onDone?: (ok: boolean) => void) => XhrHttpModule
        .createRequest<Api_CheckinTriggerDaily>(HttpMethod.POST, "checkin-trigger-daily")
        .setRelativeUrl("/v1/check-in/trigger-daily")
        .setLabel("Triggering daily check-in cron")
        .setJsonBody({})
        .setOnError(() => {
            onDone?.(false);
            return true;
        })
        .execute(() => onDone?.(true));

    /**
     * True if any signal (device or override) landed within today's *live* check-in window
     * — from 05:00 in the patient's timezone up to "now". As patients check in throughout
     * the day they move from the missed bucket to the checked-in bucket.
     */
    isCheckedInToday = (unitId: string, timezone?: string): boolean => {
        const a = this.byUnit[unitId];
        if (!a) return false;
        const tz = timezone || dayjs.tz.guess();
        const start = dayjs().tz(tz).startOf("day").add(WINDOW_START_HOUR, "hours").valueOf();
        const end = Date.now();
        const latest = Math.max(a.lastInteraction || 0, a.lastInteractionOverride || 0);
        return latest >= start && latest <= end;
    };

    /**
     * True if the device pushed its runtime status within ONLINE_THRESHOLD_MS.
     * Backed by CheckinActivityItem.lastConnected, populated by the same poll
     * that fetches morning activity — see check-in.ts for why that's bundled.
     * If lastConnected is missing (q-server hasn't shipped its half yet, or no
     * status was ever recorded for this unit) we return false: better to read
     * as "unknown / offline" than to lie about it being online.
     */
    isOnline = (unitId: string): boolean => {
        const lastConnected = this.byUnit[unitId]?.lastConnected;
        if (!lastConnected) return false;
        return Date.now() - lastConnected < ONLINE_THRESHOLD_MS;
    };
}

export const CheckinModule = new CheckinModule_Class();
